import { Menu, MenuListProps } from '@televet/kibble-ui/build/components/Menu';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box } from '@televet/kibble-ui/build/chakra';
import React, { useMemo } from 'react';
import Segment, { SegmentProps } from '../components/Segment';
import { availableSegments } from './constants/conditionalSegments.constants';
import { useConditionalSegmentsReturnType } from './hooks/useConditionalSegments';
import { ClinicConditionalSegment } from './types/conditionalSegments.types';

type ConditionalSegmentsProps = Pick<SegmentProps, 'isSelected' | 'onSelect' | 'disabled'> & {
  displayEmptyValidationMessage: boolean;
};

const ConditionalSegments = ({
  onSelect,
  isSelected,
  addCondition,
  conditions,
  removeCondition,
  updateConditionValue,
  displayEmptyValidationMessage,
  disabled,
}: ConditionalSegmentsProps & useConditionalSegmentsReturnType): JSX.Element => {
  const menuItems: MenuListProps['menuItems'] = useMemo(
    () =>
      Object.entries(availableSegments)
        .filter(([value]) => value !== 'appointmentDate')
        .map(([value, { label }]) => ({
          label,
          value,
          isDisabled: !!conditions.find(({ condition }) => condition == value),
          onSelect: ({ value }): void => {
            addCondition(value as ClinicConditionalSegment);
            onSelect();
          },
        })),
    [addCondition, conditions, onSelect],
  );

  return (
    <Segment
      title="Filter All Clients"
      description="Add conditions to filter your list of clients."
      isSelected={isSelected}
      onSelect={onSelect}
      disabled={disabled}
    >
      <Box py="2">
        <Box>
          {conditions.map((entry) => {
            const { Component, condition, segmentDefinition, displayValidation, additionalSegmentProps } = entry;
            return (
              <Component
                key={condition}
                onDelete={(): void => {
                  removeCondition(condition);
                  onSelect();
                }}
                updateConditionValue={(value: unknown): void => {
                  updateConditionValue(condition, value);
                  onSelect();
                }}
                conditionValue={segmentDefinition}
                displayValidation={displayValidation}
                {...additionalSegmentProps}
                disabled={disabled}
              />
            );
          })}
        </Box>

        {displayEmptyValidationMessage && (
          <Text as="p" color="text.danger">
            Please add at least one condition.
          </Text>
        )}

        <Menu
          buttonProps={{
            variant: 'ghost',
            size: 'md',
            leftIconName: 'plus',
            showRightIcon: false,
            mt: '3',
            text: 'Add Condition',
          }}
          closeOnSelect={false}
          listProps={{
            shouldUsePortal: true,
            menuItems,
          }}
        />
      </Box>
    </Segment>
  );
};

export default ConditionalSegments;
